import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatures } from '@/contexts/FeaturesContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  TrendingUp, Eye, MousePointerClick, ArrowRight,
  Link2, Image, Settings, ChevronRight, Loader2,
} from 'lucide-react';
import { axiosInstance } from '@/lib/axios';
import { config } from '@/config';
import { format } from 'date-fns';

// ─── types ────────────────────────────────────────────────────────────────────

interface QuickStats {
  totalLeads: number;
  profileViews: number;
  bookingClicks: number;
  conversionRate: number;
}

interface RecentLead {
  _id: string;
  eventType: string;
  source: string;
  timestamp: string;
  converted: boolean;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function leadSentence(eventType: string, source: string): string {
  const from = source === 'map' ? 'the map' : source === 'search' ? 'a search' :
               source === 'list' ? 'the listing' : 'a direct link';
  if (eventType === 'booking_click') return `Clicked "Visit Website" from ${from}`;
  if (eventType === 'map_click')     return `Clicked your pin on the map`;
  return `Visited your profile from ${from}`;
}

function leadIcon(eventType: string): string {
  if (eventType === 'booking_click') return '🎯';
  if (eventType === 'map_click')     return '📍';
  return '👀';
}

// ─── stat mini-card ───────────────────────────────────────────────────────────

function MiniStat({ label, value, icon, color }: {
  label: string; value: number | string; icon: React.ReactNode; color: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm p-4 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
        {icon}
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900 leading-tight">{value}</p>
        <p className="text-xs text-slate-400 font-medium">{label}</p>
      </div>
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export function BusinessHomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const features = useFeatures();

  const [businessName, setBusinessName]     = useState('');
  const [isPartner, setIsPartner]           = useState(false);
  const [bookingUrl, setBookingUrl]         = useState('');
  const [bookingMode, setBookingMode]       = useState<'platform' | 'redirect'>('platform');
  const [stats, setStats]                   = useState<QuickStats | null>(null);
  const [recentLeads, setRecentLeads]       = useState<RecentLead[]>([]);
  const [loading, setLoading]               = useState(true);

  useEffect(() => {
    // Fetch business profile
    const token = (() => {
      try {
        const ba = JSON.parse(localStorage.getItem('businessAuth') || '{}');
        const pa = JSON.parse(localStorage.getItem('auth') || '{}');
        return (ba.token ? ba : pa).token || '';
      } catch { return ''; }
    })();

    Promise.all([
      fetch(`${config.apiUrl}/business/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()).catch(() => null),

      // Partner analytics — 7 day quick view
      axiosInstance.get('/partner/analytics?days=7')
        .then(r => r.data)
        .catch(() => null),

      // Recent leads — last 5
      axiosInstance.get('/partner/leads?page=1&limit=5&days=7')
        .then(r => r.data)
        .catch(() => null),
    ]).then(([profile, analytics, leads]) => {
      if (profile?.data) {
        setBusinessName(profile.data.name ?? '');
        setIsPartner(profile.data.isPartnerListing ?? false);
        setBookingUrl(profile.data.contact?.bookingUrl ?? '');
        setBookingMode(profile.data.contact?.bookingMode ?? 'platform');
      }
      if (analytics?.summary) setStats(analytics.summary);
      if (leads?.leads)       setRecentLeads(leads.leads.slice(0, 4));
    }).finally(() => setLoading(false));
  }, []);

  const name = businessName || user?.name || 'your business';

  // ── quick action cards ──────────────────────────────────────────────────────
  const actions = [
    {
      icon: <Link2 className="w-5 h-5 text-emerald-600" />,
      bg:   'bg-emerald-100',
      title: 'Booking Settings',
      desc:  bookingMode === 'redirect' && bookingUrl
        ? `Redirecting to ${new URL(bookingUrl).hostname}`
        : 'Configure your booking link',
      path: '/business/settings',
      hash: '#booking',
    },
    {
      icon: <Image className="w-5 h-5 text-blue-600" />,
      bg:   'bg-blue-100',
      title: 'Activities',
      desc:  'Manage your experiences and photos',
      path: '/business/activities',
    },
  ].filter(Boolean);

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-3xl mx-auto">

      {/* ── Welcome header ── */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 leading-tight">
            Welcome back 👋
          </h1>
          <p className="text-slate-500 text-sm mt-0.5 capitalize">{name}</p>
        </div>
        {isPartner && (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 shrink-0 mt-1">
            Partner Listing
          </Badge>
        )}
      </div>

      {/* ── Quick stats (7 days) ── */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
        </div>
      ) : stats ? (
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2.5">
            Leads from Wildy — last 7 days
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MiniStat label="Leads"       value={stats.totalLeads}    icon={<TrendingUp className="w-4 h-4 text-emerald-600" />} color="bg-emerald-100" />
            <MiniStat label="Views"       value={stats.profileViews}  icon={<Eye className="w-4 h-4 text-blue-600" />}          color="bg-blue-100" />
            <MiniStat label="Clicks"      value={stats.bookingClicks} icon={<MousePointerClick className="w-4 h-4 text-violet-600" />} color="bg-violet-100" />
            <MiniStat label="Conversion"  value={`${stats.conversionRate}%`} icon={<ArrowRight className="w-4 h-4 text-amber-600" />} color="bg-amber-100" />
          </div>
          <Link to="/business/partner-stats"
            className="flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-700 font-medium mt-2.5 transition-colors">
            <TrendingUp className="w-3.5 h-3.5" />
            View full Partner Stats
            <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      ) : (
        <div className="bg-slate-50 rounded-xl border border-slate-200 px-5 py-6 text-center text-sm text-slate-400">
          No lead data yet. Leads appear once visitors interact with your listing.
        </div>
      )}

      {/* ── Quick actions ── */}
      <div>
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2.5">Quick actions</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {actions.map(a => (
            <button key={a.path}
              onClick={() => navigate(a.path)}
              className="flex items-center gap-3 bg-white rounded-xl border border-slate-200/60 shadow-sm p-4 hover:shadow-md hover:border-slate-300 transition-all text-left group">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${a.bg}`}>
                {a.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 text-sm">{a.title}</p>
                <p className="text-xs text-slate-400 mt-0.5 truncate">{a.desc}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 shrink-0 transition-colors" />
            </button>
          ))}
        </div>
      </div>

      {/* ── Recent activity ── */}
      {recentLeads.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2.5">Recent visitors</p>
          <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm divide-y divide-slate-50">
            {recentLeads.map(lead => (
              <div key={lead._id} className="flex items-start gap-3 px-4 py-3">
                <span className="text-base mt-0.5 shrink-0">{leadIcon(lead.eventType)}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-700 leading-snug">
                    {leadSentence(lead.eventType, lead.source)}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {format(new Date(lead.timestamp), 'MMM d, HH:mm')}
                  </p>
                </div>
                {lead.converted && (
                  <span className="text-xs font-semibold text-emerald-600 shrink-0 mt-0.5">Booked</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Settings shortcut ── */}
      <div className="pt-1">
        <button onClick={() => navigate('/business/settings')}
          className="flex items-center gap-2 text-xs text-slate-400 hover:text-slate-600 transition-colors">
          <Settings className="w-3.5 h-3.5" />
          Business Settings
        </button>
      </div>

    </div>
  );
}
